import { Injectable } from '@angular/core';
import { AuthService } from '../pages/auth/authServices';

@Injectable({
  providedIn: 'root'
})
export class GoogleUrlService {

  constructor(private authService: AuthService) {}

  /**
   * Generates Google Drive URL with authuser parameter
   * @param fileId - Google Drive file ID
   * @param viewType - Type of view ('view', 'preview', 'edit')
   * @returns Complete Google Drive URL with authuser parameter
   */
  getDriveUrl(fileId: string, viewType: 'view' | 'preview' | 'edit' = 'view'): string {
    const userEmail = this.authService.getUserEmail();
    if (!userEmail) {
      return `https://drive.google.com/file/d/${fileId}/${viewType}`;
    }

    return `https://drive.google.com/file/d/${fileId}/${viewType}?authuser=${encodeURIComponent(userEmail)}`;
  }

  /**
   * Generates Google Calendar URL with authuser parameter
   * @param calendarId - Google Calendar ID
   * @param additionalParams - Additional query parameters
   * @returns Complete Google Calendar URL with authuser parameter
   */
  getCalendarUrl(calendarId: string, additionalParams: string = ''): string {
    const userEmail = this.authService.getUserEmail();
    if (!userEmail) {
      return `https://calendar.google.com/calendar/embed?src=${encodeURIComponent(calendarId)}${additionalParams}`;
    }

    const authuserParam = `&authuser=${encodeURIComponent(userEmail)}`;
    return `https://calendar.google.com/calendar/embed?src=${encodeURIComponent(calendarId)}${additionalParams}${authuserParam}`;
  }

  /**
   * Generates Google Calendar URL with multiple calendar IDs and authuser parameter
   * @param calendarIds - Array of Google Calendar IDs
   * @param additionalParams - Additional query parameters
   * @returns Complete Google Calendar URL with authuser parameter
   */
  getMultiCalendarUrl(calendarIds: string[], additionalParams: string = ''): string {
    return this.getCalendarEmbedUrl(calendarIds, additionalParams);
  }

  /**
   * Google Calendar embed URL. With no calendar IDs, loads the month grid without
   * physician (or other) calendar sources — used for initial Calendar view.
   */
  getCalendarEmbedUrl(calendarIds: string[] = [], additionalParams: string = ''): string {
    const userEmail = this.authService.getUserEmail();
    const authuserParam = userEmail
      ? `&authuser=${encodeURIComponent(userEmail)}`
      : '';
    const srcParams = calendarIds
      .map((id) => `src=${encodeURIComponent(id)}`)
      .join('&');
    const query = srcParams
      ? `${srcParams}${additionalParams}${authuserParam}`
      : `mode=MONTH${additionalParams}${authuserParam}`;
    return `https://calendar.google.com/calendar/embed?${query}`;
  }

  /**
   * Generates Google Drive download URL with authuser parameter
   * @param fileId - Google Drive file ID
   * @returns Complete Google Drive download URL with authuser parameter
   */
  getDriveDownloadUrl(fileId: string): string {
    const userEmail = this.authService.getUserEmail();
    if (!userEmail) {
      return `https://drive.google.com/uc?export=download&id=${fileId}`;
    }

    return `https://drive.google.com/uc?export=download&id=${fileId}&authuser=${encodeURIComponent(userEmail)}`;
  }

  /**
   * Generates Google Drive folder URL with authuser parameter
   * @param folderId - Google Drive folder ID
   * @returns Complete Google Drive folder URL with authuser parameter
   */
  getDriveFolderUrl(folderId: string): string {
    const userEmail = this.authService.getUserEmail();
    if (!userEmail) {
      return `https://drive.google.com/drive/folders/${folderId}`;
    }

    return `https://drive.google.com/drive/folders/${folderId}?authuser=${encodeURIComponent(userEmail)}`;
  }

  /**
   * Adds authuser parameter to existing Google URL
   * @param url - Existing Google URL
   * @returns URL with authuser parameter added
   */
  addAuthUserToUrl(url: string): string {
    const userEmail = this.authService.getUserEmail();
    if (!userEmail) {
      return url;
    }

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}authuser=${encodeURIComponent(userEmail)}`;
  }

  /**
   * Extracts a Google Drive folder ID from a full URL or returns the value as-is if already an ID.
   */
  extractDriveFolderId(value: string | null | undefined): string {
    if (value === undefined || value === null) {
      return '';
    }

    const trimmed = String(value).trim();
    if (!trimmed) {
      return '';
    }

    const folderMatch = trimmed.match(
      /drive\.google\.com\/drive\/folders\/([a-zA-Z0-9_-]+)/
    );
    if (folderMatch) {
      return folderMatch[1];
    }

    if (trimmed.includes('drive.google.com')) {
      const idMatch = trimmed.match(/[?&]id=([a-zA-Z0-9_-]+)/);
      if (idMatch) {
        return idMatch[1];
      }
    }

    return trimmed;
  }
}
